import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { Header } from '../../components/Header';
import { PageMetadata } from '../../components/PageMetadata';
import { Footer } from '../../components/Footer/Footer';
import { Section, Row, Column } from '../../components/Section';
import { CallToActionSection } from '../../components/CallToActionSection';

import { GetServerPageProps } from '../../lib/apollo';
import {
  getServerPageIndividualFamily,
  getServerPageIndividualFamilyArticlesSection,
  getServerPageIndividualFamilyCommonQuestionsSection,
  getServerPageIndividualFamilyLocationSearchSection,
  getServerPageIndividualFamilyLogosSection,
} from './IndividualFamily.generated';
import { CommonQuestions } from '../../components/CommonQuestions/CommonQuestions';
import { Hero } from '../../components/Hero';
import { heroData } from '../../components/heroData';
import { getServerPageLocationSearch } from '../../components/LocationSearch.generated';
import { LocationSearch } from '../../components/LocationSearch';
import { ProductLogos } from '../../components/ProductLogos';
import { FeaturedArticles } from '../../components/FeaturedArticles';
import { Text } from '../../components/Text';
import { RichText } from '../../lib/copyRendering';
import Image from 'next/image';

export default function IndividualFamily({
  pageData,
  heroData,
  logosData,
  locationSearchContent,
  locationSearch,
  commonQuestionsData,
  articlesData,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  if (!pageData) {
    throw new Error('Unable to load page');
  }

  const locationSearchContentData =
    locationSearchContent.snippetCollection?.items[0];

  return (
    <>
      <PageMetadata
        pageTitle={pageData.seoTitle ?? undefined}
        description={pageData.seoDescription ?? undefined}
        openGraphImage={pageData.openGraphImage?.url ?? undefined}
      />
      <Header planType="individual-family" />
      <Hero
        heading={heroData.heading}
        subheading={heroData.subheading}
        image={{
          url: pageData.heroImage?.url ?? '',
          description: pageData.heroImage?.description ?? '',
        }}
        aggregatedReviews={heroData.aggregatedReviews}
      />
      <Section background="WHITE">
        <Row inset className="items-center">
          <Column>
            <Text.H2 className="font-bold !mb-0">
              {locationSearchContentData?.name}
            </Text.H2>
            <Text.H4 className="!text-gray-700 font-semibold">
              {locationSearchContentData?.subtitle}
            </Text.H4>
            <RichText content={locationSearchContentData?.content} />
            <LocationSearch
              data={locationSearch}
              linkPrefix={pageData.slug ?? ''}
            />
          </Column>
          {locationSearchContentData?.image && (
            <Column>
              <Image
                src={locationSearchContentData.image.url ?? ''}
                alt={locationSearchContentData.image.description ?? ''}
                width={400}
                height={151}
                className="sm:pl-4"
              />
            </Column>
          )}
        </Row>
      </Section>
      <CallToActionSection
        title="Find your most affordable health plan"
        subtitle="Enroll in under 10 minutes."
        quoteAction
        quoteActionLabel="Get Pricing Information"
      />

      <CommonQuestions
        snippetGroup={commonQuestionsData.snippetGroupCollection?.items[0]}
        inset
        blueBackground
      />
      <FeaturedArticles
        snippetGroup={
          articlesData.snippetGroupCollection?.items[0] ?? undefined
        }
      />
      <CallToActionSection quoteAction />
      <ProductLogos
        snippetGroup={logosData.snippetGroupCollection?.items[0] ?? undefined}
        className="hidden md:block"
      />
      <Footer />
    </>
  );
}

export const getStaticProps: GetStaticProps<{
  pageData:
    | NonNullable<
        NonNullable<
          GetServerPageProps<typeof getServerPageIndividualFamily>['data']
        >['pageCollection']
      >['items'][0]
    | undefined;
  heroData: Awaited<ReturnType<typeof heroData>>;
  logosData: GetServerPageProps<
    typeof getServerPageIndividualFamilyLogosSection
  >['data'];
  locationSearchContent: GetServerPageProps<
    typeof getServerPageIndividualFamilyLocationSearchSection
  >['data'];
  locationSearch: GetServerPageProps<
    typeof getServerPageLocationSearch
  >['data'];
  commonQuestionsData: GetServerPageProps<
    typeof getServerPageIndividualFamilyCommonQuestionsSection
  >['data'];
  articlesData: GetServerPageProps<
    typeof getServerPageIndividualFamilyArticlesSection
  >['data'];
}> = async () => {
  return {
    props: {
      pageData: (await getServerPageIndividualFamily({})).props.data
        .pageCollection?.items[0],
      heroData: await heroData(),
      logosData: (await getServerPageIndividualFamilyLogosSection({})).props
        .data,
      locationSearchContent: (
        await getServerPageIndividualFamilyLocationSearchSection({})
      ).props.data,
      locationSearch: (await getServerPageLocationSearch({})).props.data,
      commonQuestionsData: (
        await getServerPageIndividualFamilyCommonQuestionsSection({})
      ).props.data,
      articlesData: (await getServerPageIndividualFamilyArticlesSection({}))
        .props.data,
    },
  };
};
